import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { CollapseBlock } from '../../../../common/index.jsx';
import { useTranslation } from 'react-i18next';
import Form from '../../../../antd/form/Form/Form.jsx';
import FormTextField from '../../../../antd/form/FormTextField/FormTextField.jsx';
import FormSwitch from '../../../../antd/form/FormSwitch/FormSwitch.jsx';
import FormSelect from '../../../../antd/form/FormSelect/FormSelect.jsx';
import FormButton from '../../../../antd/form/FormButton/FormButton.jsx';
import FormButtonsContainer from '../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import { required } from '../../../../antd/form/FormValidation.js';
import { Form as AntForm } from 'antd';
import { useGetStudyProgramsIngAllQuery } from '../../../../../redux/services/study_programs.jsx';
import { ROLE_ING_PROJECTS_ASSIGNMENT_COORDINATOR } from '../../../../../utils/constants/roles.js';

const UserForm = ({ roles, onSubmit, initials }) => {
  const { t } = useTranslation();

  const rolesOptions = useMemo(
    () =>
      roles &&
      [...roles]
        .sort((left, right) => (left.name || '').localeCompare(right.name || '', 'sk'))
        .map((item) => ({
          value: item.id,
          label: item.name,
        })),
    [roles],
  );

  const [form] = AntForm.useForm();
  const isEmailSynced = AntForm.useWatch('ldap_mail', form);
  const chosenRolesIds = AntForm.useWatch('chosenRolesIds', form);

  const coordinatorRoleId = useMemo(
    () => roles?.find((role) => role.name === ROLE_ING_PROJECTS_ASSIGNMENT_COORDINATOR)?.id,
    [roles],
  );
  const isCoordinatorChosen = Boolean(
    coordinatorRoleId && (chosenRolesIds || initials.chosenRolesIds || []).includes(coordinatorRoleId),
  );

  const { data: ingStudyPrograms, isLoading: isLoadingIngStudyPrograms } =
    useGetStudyProgramsIngAllQuery(undefined, { skip: !isCoordinatorChosen });

  const ingStudyProgramsOptions = useMemo(
    () =>
      (ingStudyPrograms || []).map((studyProgram) => ({
        value: studyProgram.id,
        label: studyProgram.shortcut
          ? `${studyProgram.name} (${studyProgram.shortcut})`
          : studyProgram.name,
      })),
    [ingStudyPrograms],
  );

  return (
    <>
      <Form onFinish={onSubmit} initialValues={initials} form={form}>
        {initials.teacher && (
          <CollapseBlock title={t('Profil')} collapsible>
            <FormTextField
              label={t('Meno a priezvisko')}
              name={'fullName'}
              disabled
            />
            <FormTextField
              label={t('Kontaktný email')}
              name={'mail'}
              rules={!isEmailSynced ? [required(t)] : []}
              disabled={isEmailSynced}
            />
            <FormSwitch
              label={t(
                'Synchronizovať e-mailovú adresu s nastavením na LDAP serveri',
              )}
              name="ldap_mail"
            />
            <FormSwitch label={t('Newsletter')} name="newsletters" />
          </CollapseBlock>
        )}

        <CollapseBlock title={t('Role')}>
          <FormTextField
            label={t('Meno a priezvisko')}
            name={'rolesFullName'}
            disabled
          />

          {rolesOptions && (
            <FormSelect
              label={t('Role používateľa')}
              name={'chosenRolesIds'}
              placeholder={t('Vyhľadať rolu')}
              options={rolesOptions}
              mode="multiple"
            />
          )}

          {isCoordinatorChosen && (
            <FormSelect
              label={t('Inž. študijné programy')}
              name={'ipStudyProgramIds'}
              placeholder={t('Vyberte študijné programy')}
              options={ingStudyProgramsOptions}
              loading={isLoadingIngStudyPrograms}
              mode="multiple"
              rules={[required(t)]}
              extra={t('Študijné programy, ktorých priradenie študentov na inž. projekty koordinátor spravuje.')}
            />
          )}
        </CollapseBlock>

        <FormButtonsContainer>
          <FormButton submitOnClick={true} submitKey={'users'}>
            {t('Uložiť')}
          </FormButton>
        </FormButtonsContainer>
      </Form>
    </>
  );
};

UserForm.propTypes = {
  roles: PropTypes.array,
  initials: PropTypes.object.isRequired,
  onSubmit: PropTypes.func,
};

export default UserForm;
